// Kanemori Sanctuary countryside (M10, owner playtest 2026-10-17): the winding mountain road, the walkable forest and
// what lies along the drive: a sakura stretch, a strolling garden (koi pond, red bridge, tea house), an outdoor onsen,
// terraced paddies, a stream with a waterfall, a bamboo grove, a fox shrine with its torii row, the old pilgrim stair
// and a rest stop with vending machines. Called from hd-sanctuary-place.ts: paintCountry on the ground (before the
// baked lights), countryProps with the upright props. Collision is tile based and lives in world.json; the road ribbon
// is painted smooth along the same centre line the collision corridor is built from (common/src/country.ts).
// Palette: hd-kit ramps of theme.ts only. Presentation only.
import { WORLD, type Rect } from '../../common/src/data';
import { T, TILE, tileAt, type GameMap } from '../../common/src/map';
import { sampleRoad, countryField, FEATURES, featureAt, type Feature } from '../../common/src/country';
import { hash2 } from '../../common/src/util';
import { canvas } from './canvas';
import { HT, K, type G, px, speckle, disc, dk, lt, mix, alpha, shadow } from './hd-kit';
import type { HdLight } from './hd-scene';
import { SK, COUNTRY_FLOOR } from './hd-sanctuary';
import { countryPlan, featurePath, COUNTRY_Y0, type Tree } from '../../common/src/country-plan';

/** a palette colour pushed away from grey (k > 1): amber and cyan mix to a grey-green, and the Low comfort grade
 *  halves what is left, so bamboo and tea would read as stone; this keeps them green. Still derived from the palette. */
const vivid = (hex: string, k: number) => {
  const c = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16)), l = 0.3 * c[0] + 0.59 * c[1] + 0.11 * c[2];
  return '#' + c.map(v => Math.max(0, Math.min(255, Math.round(l + (v - l) * k))).toString(16).padStart(2, '0')).join('');
};
/** countryside materials: ramps of the palette */
export const CK = {
  floor: COUNTRY_FLOOR.floor, floorHi: COUNTRY_FLOOR.floorHi, litter: COUNTRY_FLOOR.litter,
  grass: mix(SK.mossLo, SK.cedarLo, 0.35), grassHi: mix(SK.moss, SK.cedarLo, 0.2), grassLo: mix(SK.mossLo, SK.cedarDk, 0.5),
  gravel: mix(SK.stoneLo, SK.mossLo, 0.25), gravelHi: mix(SK.stone, SK.mossLo, 0.2),
  water: dk(K.cyan, 0.74), waterHi: dk(K.cyan, 0.52), waterLo: dk(K.cyan, 0.86), foam: lt(K.cyan, 0.55),
  sak: lt(K.pink, 0.42), sakHi: lt(K.pink, 0.68), sakLo: mix(K.pink, K.slate, 0.38), sakDk: dk(mix(K.pink, K.slate, 0.4), 0.35),
  maple: dk(mix(K.pink, K.amber, 0.42), 0.12), mapleHi: lt(mix(K.pink, K.amber, 0.55), 0.18), mapleLo: dk(K.pink, 0.48),
  pine: mix(SK.cedar, SK.moss, 0.25), pineHi: mix(SK.cedarHi, SK.mossHi, 0.4), pineLo: SK.cedarLo,
  broad: mix(SK.moss, SK.cedar, 0.4), broadHi: SK.mossHi, broadLo: mix(SK.mossLo, SK.cedarLo, 0.4),
  bamboo: vivid(dk(mix(K.amber, K.cyan, 0.4), 0.22), 2.6), bambooHi: vivid(lt(mix(K.amber, K.cyan, 0.35), 0.05), 2.4), bambooLo: vivid(dk(mix(K.amber, K.cyan, 0.4), 0.55), 2.4), // green stalks, still green once the comfort grade mutes the ground
  bark: dk(K.wood, 0.25), barkHi: K.woodLo, rice: lt(mix(SK.mossHi, K.amber, 0.3), 0.2), riceLo: mix(SK.mossHi, K.amber, 0.15),
  cedarWood: mix(K.woodHi, K.amber, 0.15), hinoki: lt(K.woodHi, 0.12),
  tea: vivid(dk(mix(K.amber, K.cyan, 0.4), 0.35), 2.6), teaHi: vivid(dk(mix(K.amber, K.cyan, 0.35), 0.1), 2.4), teaLo: vivid(dk(mix(K.amber, K.cyan, 0.42), 0.6), 2.4), teaTip: vivid(lt(mix(K.amber, K.cyan, 0.3), 0.15), 2),
  soil: mix(K.wood, SK.mossLo, 0.35), soilHi: mix(K.woodHi, SK.mossLo, 0.4), soilLo: dk(mix(K.wood, SK.mossLo, 0.4), 0.45),
};

/** the tiles of the scene rect R that land on the canvas piece being painted (null in the light-only dummy context) */
function tileWindow(g: G, R: Rect) {
  const c = (g as { canvas?: { width: number; height: number } }).canvas; if (!c || typeof g.getTransform !== 'function') return null;
  const tr = g.getTransform();
  const x0 = Math.max(R.x, R.x + Math.floor(-tr.e / HT) - 1), y0 = Math.max(R.y, R.y + Math.floor(-tr.f / HT) - 1);
  const x1 = Math.min(R.x + R.w, R.x + Math.ceil((-tr.e + c.width) / HT) + 1), y1 = Math.min(R.y + R.h, R.y + Math.ceil((-tr.f + c.height) / HT) + 1);
  return x1 > x0 && y1 > y0 ? { x0, y0, x1, y1 } : null;
}

// ================================================================== ground tiles
/** a round lawn or clearing in the oval of a feature's rect, its texture clipped to that oval (never a box of tiles) */
function lawn(g: G, x: number, y: number, w: number, h: number, base: string, hi: string, lo: string, flowers: boolean, seed: number) {
  g.save(); g.beginPath(); g.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2); g.clip();
  px(g, x, y, w, h, base); speckle(g, x, y, w, h, hi, 0.1, seed, 2); speckle(g, x, y, w, h, lo, 0.1, seed + 1, 3);
  if (flowers) { speckle(g, x, y, w, h, SK.paper, 0.006, seed + 2, 2); speckle(g, x, y, w, h, CK.sakHi, 0.006, seed + 3, 2); }
  g.restore();
}
/** One floor for every walkable countryside tile, flat in colour and even in texture, so no tile ever reads as a square
 *  (lawns, clearings and the fallen petals are drawn as smooth shapes or live effects on top). */
function forestFloor(g: G, x: number, y: number, tx: number, ty: number) {
  const v = hash2(tx * 3 + 1, ty * 5 + 2);
  px(g, x, y, HT, HT, CK.floor);
  speckle(g, x, y, HT, HT, CK.floorHi, 0.12, tx * 7 + ty, 2);
  speckle(g, x, y, HT, HT, CK.litter, 0.05, tx + ty * 9, 2);
  speckle(g, x, y, HT, HT, alpha(K.night, 0.5), 0.06, tx * 11 + ty * 3, 3);
  if (v > 0.86) for (let k = 0; k < 5; k++) px(g, x + 8 + k * 3, y + 16 - (k % 2) * 4, 1, 6, SK.moss); // fern
  else if (v < 0.07) { disc(g, x + 14, y + 18, 6, 4, K.ink); disc(g, x + 14, y + 17, 5, 3, SK.stoneLo); px(g, x + 10, y + 15, 5, 1, SK.stone); } // stone
  else if (v > 0.8) { px(g, x + 4, y + 22, 20, 2, CK.bark); px(g, x + 4, y + 22, 20, 1, CK.barkHi); } // a root
}
function paddyTile(g: G, x: number, y: number, tx: number, ty: number, f: Feature) {
  // terraces three tiles deep, each a still sheet of water with rows of young rice, banked by an earth bund (aze)
  const row = Math.floor((ty - f.y) / 3), top = (ty - f.y) % 3 === 0, bottom = (ty - f.y) % 3 === 2 || ty === f.y + f.h - 1;
  const deep = row % 2 ? CK.water : mix(CK.water, CK.waterLo, 0.4);
  px(g, x, y, HT, HT, deep);
  for (let j = 3; j < HT; j += 8) for (let i = 2 + ((j >> 3) & 1) * 4; i < HT; i += 8) { px(g, x + i, y + j, 2, 4, CK.riceLo); px(g, x + i, y + j, 1, 3, CK.rice); }
  if (hash2(tx, ty * 3) < 0.25) px(g, x + 6, y + 12, 12, 1, alpha(CK.foam, 0.35)); // a glint
  if (top) { px(g, x, y, HT, 5, K.ink); px(g, x, y, HT, 4, CK.litter); px(g, x, y, HT, 1, lt(CK.litter, 0.2)); }
  if (bottom) px(g, x, y + HT - 3, HT, 3, alpha(K.night, 0.5));
  if (tx === f.x) { px(g, x, y, 5, HT, CK.litter); px(g, x + 5, y, 1, HT, K.ink); }
  if (tx === f.x + f.w - 1) { px(g, x + HT - 5, y, 5, HT, CK.litter); px(g, x + HT - 6, y, 1, HT, K.ink); }
}
function bambooFloor(g: G, x: number, y: number, tx: number, ty: number) {
  forestFloor(g, x, y, tx, ty);
  for (let k = 0; k < 3; k++) { const a = hash2(tx + k, ty * 2); px(g, x + Math.floor(a * 24), y + Math.floor(hash2(k, tx) * 28), 5, 1, CK.bambooLo); } // fallen leaves
}

// ================================================================== the road
function ribbon(g: G, R: Rect) {
  const road = WORLD.sanctuary?.road; if (!road) return;
  const S = sampleRoad(road, 0.25), H = road.half * HT;
  const X = (x: number) => (x - R.x) * HT, Y = (y: number) => (y - R.y) * HT;
  const line = (off: number, w: number, c: string, dash?: number[]) => {
    g.save(); g.strokeStyle = c; g.lineWidth = w; g.lineJoin = 'round'; g.lineCap = 'butt'; if (dash) g.setLineDash(dash);
    g.beginPath();
    S.forEach((s, i) => { const n = s.a + Math.PI / 2, x = X(s.x) + Math.cos(n) * off, y = Y(s.y) + Math.sin(n) * off; if (i) g.lineTo(x, y); else g.moveTo(x, y); });
    g.stroke(); g.restore();
  };
  line(0, 2 * H + 10, alpha(K.night, 0.55)); // the shoulder's shade
  line(0, 2 * H + 4, K.ink);
  line(0, 2 * H, K.asphalt);
  // wear: a darker strip down each wheel track, the lighter crown between
  line(-H * 0.45, 6, K.asphaltLo); line(H * 0.45, 6, K.asphaltLo); line(0, 8, K.asphaltHi);
  // edge lines and the centre dash follow the curve
  line(-(H - 5), 2, K.paint); line(H - 5, 2, K.paint);
  line(0, 2, K.lane, [12, 12]);
  // grain: specks and patches scattered along the ribbon
  for (let i = 0; i < S.length; i += 2) {
    const s = S[i], n = s.a + Math.PI / 2;
    for (let k = 0; k < 3; k++) {
      const o = (hash2(i, k) - 0.5) * 2 * (H - 8), x = Math.round(X(s.x) + Math.cos(n) * o), y = Math.round(Y(s.y) + Math.sin(n) * o);
      px(g, x, y, 1, 1, k ? K.asphaltHi : K.asphaltLo);
    }
    if (hash2(i * 3, 7) < 0.02) { const o = (hash2(i, 9) - 0.5) * H, x = Math.round(X(s.x) + Math.cos(n) * o), y = Math.round(Y(s.y) + Math.sin(n) * o); disc(g, x, y, 7, 5, K.asphaltHi); speckle(g, x - 7, y - 5, 14, 10, K.asphalt, 0.3, i); }
  }
  // the road's end inside the sanctuary courtyard: a flagstone kerb square across the asphalt, so it meets the gravel cleanly
  const e = S[S.length - 1];
  g.save(); g.translate(X(e.x), Y(e.y)); g.rotate(e.a);
  g.fillStyle = K.ink; g.fillRect(-1, -H - 2, 7, 2 * H + 4);
  g.fillStyle = SK.stoneLo; g.fillRect(0, -H - 1, 5, 2 * H + 2);
  g.fillStyle = SK.stoneHi; g.fillRect(0, -H - 1, 1, 2 * H + 2);
  g.restore();
}

/** a sampled feature path drawn as a ribbon of stones, steps or water */
function pathStroke(g: G, R: Rect, f: Feature, w: number, c: string, dash?: number[]) {
  const S = featurePath(f); if (!S.length) return;
  g.save(); g.strokeStyle = c; g.lineWidth = w; g.lineJoin = 'round'; g.lineCap = 'round'; if (dash) g.setLineDash(dash);
  g.beginPath(); S.forEach((s, i) => { const x = (s.x - R.x) * HT, y = (s.y - R.y) * HT; if (i) g.lineTo(x, y); else g.moveTo(x, y); }); g.stroke(); g.restore();
}
function stream(g: G, R: Rect, f: Feature) {
  pathStroke(g, R, f, 30, alpha(K.night, 0.5));
  pathStroke(g, R, f, 26, SK.stoneLo); // pebble banks
  pathStroke(g, R, f, 20, CK.waterLo);
  pathStroke(g, R, f, 14, CK.water);
  pathStroke(g, R, f, 2, alpha(CK.waterHi, 0.9), [6, 10]);
  for (const s of featurePath(f)) if (hash2(Math.round(s.s * 4), 3) < 0.22) { // pebbles on the banks
    const n = s.a + Math.PI / 2, sd = hash2(Math.round(s.s * 4), 5) < 0.5 ? -1 : 1, x = (s.x - R.x) * HT + Math.cos(n) * 11 * sd, y = (s.y - R.y) * HT + Math.sin(n) * 11 * sd;
    disc(g, Math.round(x), Math.round(y), 3, 2, SK.stone);
  }
}
function pilgrimStair(g: G, R: Rect, f: Feature) {
  pathStroke(g, R, f, 64, alpha(K.night, 0.35));
  pathStroke(g, R, f, 58, SK.stoneDk);
  pathStroke(g, R, f, 52, SK.stoneLo);
  // a tread every half tile, square to the path, lit at its front lip; moss in the joints
  const S = featurePath(f);
  for (let i = 0; i < S.length; i += 2) {
    const s = S[i], n = s.a + Math.PI / 2, cx = (s.x - R.x) * HT, cy = (s.y - R.y) * HT;
    g.save(); g.strokeStyle = (i / 2) % 2 ? SK.stone : mix(SK.stone, SK.stoneHi, 0.4); g.lineWidth = 2; g.beginPath();
    g.moveTo(cx - Math.cos(n) * 24, cy - Math.sin(n) * 24); g.lineTo(cx + Math.cos(n) * 24, cy + Math.sin(n) * 24); g.stroke(); g.restore();
    if (hash2(i, 41) < 0.3) px(g, Math.round(cx + Math.cos(n) * (hash2(i, 3) - 0.5) * 40), Math.round(cy + Math.sin(n) * (hash2(i, 3) - 0.5) * 40), 4, 2, SK.moss);
  }
}

// ================================================================== tea field
/** A green tea plantation on a gentle slope: rounded rows of clipped bushes (chaboku) following the contour, a bank of
 *  raked soil under each row, a pickers' path winding up through them to the rest hut. Drawn in the field's rounded rect. */
const TEA_ROWS = 7;
/** Centre line (tiles) of tea row r at tile x: all rows are parallel curves that follow the slope's contour, so the art
 *  (teaField) and the frost fans standing between the rows (countryProps) agree. */
function teaRowY(f: Feature, r: number, x: number) {
  const u = (x - f.x) / f.w, pitch = (f.h - 1) / TEA_ROWS;
  return f.y + 0.85 + (r + 0.5) * pitch + Math.sin(u * Math.PI * 1.3 + 0.4) * 0.3 + u * 0.35;
}
function teaField(g: G, R: Rect, f: Feature) {
  const X0 = (f.x - R.x) * HT, Y0 = (f.y - R.y) * HT, W = f.w * HT, H = f.h * HT, rr = 22;
  const outline = () => {
    g.beginPath(); g.moveTo(X0 + rr, Y0); g.lineTo(X0 + W - rr, Y0); g.quadraticCurveTo(X0 + W, Y0, X0 + W, Y0 + rr); g.lineTo(X0 + W, Y0 + H - rr); g.quadraticCurveTo(X0 + W, Y0 + H, X0 + W - rr, Y0 + H);
    g.lineTo(X0 + rr, Y0 + H); g.quadraticCurveTo(X0, Y0 + H, X0, Y0 + H - rr); g.lineTo(X0, Y0 + rr); g.quadraticCurveTo(X0, Y0, X0 + rr, Y0); g.closePath();
  };
  g.save(); outline(); g.clip();
  px(g, X0, Y0, W, H, CK.soil); speckle(g, X0, Y0, W, H, CK.soilHi, 0.1, 5, 2); speckle(g, X0, Y0, W, H, CK.soilLo, 0.08, 6, 3);
  // Tea is clipped into tight, rounded hedges (chaboku) in long rows along the contour, with a narrow earth path between
  // each pair. Every row is one curve stroked in layers: shade below, dark sides, mid green, lighter fresh-leaf top.
  const x0 = f.x + 0.25, x1 = f.x + f.w - 0.25, N = Math.ceil((x1 - x0) * 4);
  const rowLine = (r: number, dy: number) => {
    g.beginPath();
    for (let i = 0; i <= N; i++) { const x = x0 + (x1 - x0) * i / N, px_ = (x - R.x) * HT, py = (teaRowY(f, r, x) - R.y) * HT + dy; if (i) g.lineTo(px_, py); else g.moveTo(px_, py); }
    g.stroke();
  };
  const SIDE = vivid(dk(CK.teaLo, 0.12), 1.5), TOP = vivid(lt(CK.teaHi, 0.14), 1.6); // shaded flanks stay deep; the top is the lighter fresh leaf
  const layer = (r: number, dy: number, w: number, c: string, dash?: number[]) => { g.strokeStyle = c; g.lineWidth = w; g.lineCap = 'round'; g.lineJoin = 'round'; g.setLineDash(dash ?? []); rowLine(r, dy); };
  for (let r = 0; r < TEA_ROWS; r++) { // north (uphill) to south: each row's shade lands on the earth path below it
    layer(r, 17, 8, alpha(CK.soilHi, 0.8)); layer(r, 17, 1, alpha(lt(CK.soilHi, 0.2), 0.8), [4, 6]); // the narrow earth path below the row, a footprint line trodden in it
    layer(r, 9, 11, alpha(K.night, 0.4)); // the hedge's shadow falling on it
    layer(r, 1, 22, K.ink);
    layer(r, 2, 20, SIDE); // the shaded sides
    layer(r, 0, 16, CK.tea);
    layer(r, -2, 10, TOP); // the sunlit, rounded top
    for (let i = 0; i <= N; i += 1) { // clipped into soft lobes: a lit bump and a dark seam between each
      const x = x0 + (x1 - x0) * i / N, bx = Math.round((x - R.x) * HT), by = Math.round((teaRowY(f, r, x) - R.y) * HT), h = hash2(i, r * 7 + 3);
      if (i % 2 === 0) { disc(g, bx, by - 3, 6, 3, TOP); px(g, bx - 3, by - 5, 5, 1, CK.teaTip); }
      else px(g, bx, by - 6, 1, 8, SIDE);
      if (h < 0.3) px(g, bx + Math.floor(h * 20) - 3, by - 7 + Math.floor(h * 10), 2, 1, CK.teaTip); // a fresh flush of new leaf
    }
  }
  g.setLineDash([]);
  // the pickers' path: trodden earth, a few flat stones in it
  pathStroke(g, R, f, 22, alpha(K.night, 0.35)); pathStroke(g, R, f, 16, mix(CK.soilHi, SK.stoneLo, 0.35)); pathStroke(g, R, f, 3, alpha(SK.stone, 0.7), [5, 14]);
  g.restore();
  // a low edge round the field: dark soil kerb with a lit top
  g.save(); outline(); g.strokeStyle = alpha(K.ink, 0.9); g.lineWidth = 3; g.stroke(); g.strokeStyle = alpha(CK.soilHi, 0.5); g.lineWidth = 1; g.translate(0, -1); g.stroke(); g.restore();
}

// ================================================================== garden, onsen, rest stop on the ground
function pond(g: G, x: number, y: number, w: number, h: number) {
  // an irregular pond over both water halves and the gap the bridge spans; stone edging, lily pads
  const cx = x + w / 2, cy = y + h / 2;
  disc(g, cx + 4, cy + 6, w / 2 + 6, h / 2 + 6, alpha(K.night, 0.45));
  disc(g, cx, cy, w / 2 + 4, h / 2 + 4, SK.stoneLo);
  for (let k = 0; k < 28; k++) { const a = k / 28 * Math.PI * 2; disc(g, Math.round(cx + Math.cos(a) * (w / 2 + 2)), Math.round(cy + Math.sin(a) * (h / 2 + 2)), 6, 4, k % 3 ? SK.stone : SK.stoneHi); }
  disc(g, cx, cy, w / 2 - 2, h / 2 - 2, CK.waterLo);
  disc(g, cx - 6, cy - 4, w / 2 - 10, h / 2 - 10, CK.water); disc(g, cx - 14, cy - 10, w / 2 - 34, h / 2 - 24, mix(CK.water, CK.waterHi, 0.4));
  for (let k = 0; k < 6; k++) { const a = hash2(k, 21), b = hash2(21, k); disc(g, Math.round(x + 20 + a * (w - 40)), Math.round(y + 20 + b * (h - 40)), 5, 3, SK.moss); px(g, Math.round(x + 20 + a * (w - 40)), Math.round(y + 20 + b * (h - 40)) - 3, 1, 3, CK.water); }
  for (let j = y + 16; j < y + h - 16; j += 14) px(g, cx - 40 + (j % 28), j, 22, 1, alpha(CK.foam, 0.25));
}
function redBridge(g: G, x: number, y: number, w: number, h: number) {
  // a taiko-bashi seen from above: a curved deck of planks (lighter at the crown of its arch), vermilion rails
  for (let j = 0; j < h; j++) {
    const crown = 1 - Math.abs(j / h - 0.5) * 2, c = mix(K.woodHi, CK.hinoki, crown * 0.8);
    px(g, x + 6, y + j, w - 12, 1, j % 6 === 0 ? K.ink : c);
  }
  for (const rx of [x + 2, x + w - 8]) {
    px(g, rx - 1, y - 6, 9, h + 12, K.ink); px(g, rx, y - 6, 7, h + 12, SK.vermHi); px(g, rx + 4, y - 6, 3, h + 12, SK.verm);
    for (let j = y; j < y + h; j += 20) { px(g, rx - 1, j, 8, 6, K.ink); px(g, rx, j + 1, 6, 4, SK.gold); } // giboshi caps
  }
  shadow(g, x + 8, y + h + 2, w - 8, 6, 0.4);
}
function steppingStones(g: G, pts: [number, number][]) {
  for (const [x, y] of pts) { disc(g, x + 2, y + 3, 8, 5, alpha(K.night, 0.4)); disc(g, x, y, 8, 5, K.ink); disc(g, x, y, 7, 4, SK.stone); px(g, x - 4, y - 3, 6, 1, SK.stoneHi); }
}
function onsenPool(g: G, x: number, y: number, w: number, h: number) {
  // a natural rock pool: big granite boulders round milky blue water; the steam is drawn live (country-fx.ts)
  disc(g, x + w / 2 + 4, y + h / 2 + 6, w / 2 + 12, h / 2 + 10, alpha(K.night, 0.4));
  disc(g, x + w / 2, y + h / 2, w / 2 + 6, h / 2 + 6, SK.stoneLo);
  disc(g, x + w / 2, y + h / 2, w / 2 - 6, h / 2 - 6, mix(CK.waterHi, K.white, 0.25));
  disc(g, x + w / 2 - 6, y + h / 2 - 4, w / 2 - 16, h / 2 - 14, mix(CK.waterHi, K.white, 0.4));
  for (let k = 0; k < 22; k++) {
    const a = k / 22 * Math.PI * 2, r = 9 + Math.floor(hash2(k, 5) * 7);
    const bx = Math.round(x + w / 2 + Math.cos(a) * (w / 2 - 2)), by = Math.round(y + h / 2 + Math.sin(a) * (h / 2 - 2));
    disc(g, bx + 2, by + 3, r, r - 3, alpha(K.night, 0.45)); disc(g, bx, by, r, r - 3, K.ink); disc(g, bx, by, r - 1, r - 4, k % 3 ? SK.stone : SK.stoneLo); px(g, bx - r + 3, by - r + 4, r, 2, SK.stoneHi);
  }
  for (let j = y + 18; j < y + h - 18; j += 10) px(g, x + 24 + (j % 20), j, 30, 1, alpha(K.white, 0.25));
  // the bamboo spout pouring in from the north rim
  px(g, x + w / 2 - 2, y - 14, 4, 22, K.ink); px(g, x + w / 2 - 1, y - 14, 2, 22, CK.bamboo); px(g, x + w / 2 - 1, y + 8, 2, 10, alpha(K.white, 0.6));
}
function onsenDeck(g: G, x: number, y: number, tx: number, ty: number) {
  px(g, x, y, HT, HT, K.woodLo);
  for (let j = 0; j < HT; j += 6) { px(g, x, y + j, HT, 5, (ty * 6 + j) % 12 ? CK.hinoki : mix(CK.hinoki, K.woodLo, 0.3)); px(g, x, y + j + 5, HT, 1, K.ink); }
  speckle(g, x, y, HT, HT, alpha(K.night, 0.25), 0.05, tx + ty, 2);
}
/** a low woven bamboo fence seen from above, along one side of a rect */
function fenceH(g: G, x: number, y: number, w: number) { px(g, x, y, w, 8, K.ink); px(g, x, y + 1, w, 5, CK.bamboo); for (let i = x; i < x + w; i += 6) px(g, i, y + 1, 1, 5, CK.bambooLo); px(g, x, y + 1, w, 1, CK.bambooHi); px(g, x, y + 8, w, 4, alpha(K.night, 0.45)); }
function fenceV(g: G, x: number, y: number, h: number) { px(g, x, y, 8, h, K.ink); px(g, x + 1, y, 5, h, CK.bamboo); for (let j = y; j < y + h; j += 6) px(g, x + 1, j, 5, 1, CK.bambooLo); px(g, x + 1, y, 1, h, CK.bambooHi); }
function restStop(g: G, x: number, y: number, w: number, h: number) {
  px(g, x, y, w, h, K.asphalt); speckle(g, x, y, w, h, K.asphaltHi, 0.06, 3); speckle(g, x, y, w, h, K.asphaltLo, 0.06, 4);
  for (let i = x + 24; i < x + w - 8; i += 48) px(g, i, y + 8, 2, h - 30, K.paint); // bays
  px(g, x + 6, y + h - 10, w - 12, 2, K.paint);
  px(g, x, y + h - 4, w, 4, SK.stoneLo); px(g, x, y + h - 4, w, 1, SK.stoneHi); // kerb
}

// ================================================================== the ground pass
export function paintCountry(g: G, m: GameMap, R: Rect) {
  const W = tileWindow(g, R); if (!W) return;
  const road = WORLD.sanctuary?.road, f = countryField(m.w, m.h);
  const X = (tx: number) => (tx - R.x) * HT, Y = (ty: number) => (ty - R.y) * HT;
  const t = (tx: number, ty: number) => tileAt(m, tx, ty);
  const feats = FEATURES();
  for (let ty = Math.max(W.y0, COUNTRY_Y0); ty < W.y1; ty++) for (let tx = Math.max(W.x0, 258); tx < Math.min(W.x1, 334); tx++) {
    const k = t(tx, ty), x = X(tx), y = Y(ty), d = f ? f[ty * m.w + tx] : Infinity;
    if (k === T.ALLEY && road && d <= road.verge + 0.01 && !WORLD.alleys.some(a => tx >= a.x && ty >= a.y && tx < a.x + a.w && ty < a.y + a.h)) { forestFloor(g, x, y, tx, ty); continue; } // the verge: the forest's floor, the asphalt is painted over it
    if (k !== T.BOLLARD && k !== T.LOW) continue;
    const ft = featureAt(tx, ty);
    if (ft?.kind === 'paddies') paddyTile(g, x, y, tx, ty, ft);
    else if (ft?.kind === 'onsen') onsenDeck(g, x, y, tx, ty);
    else if (ft?.kind === 'bamboo') bambooFloor(g, x, y, tx, ty);
    else forestFloor(g, x, y, tx, ty);
  }
  // features with shapes of their own, over the tiles
  for (const ft of feats) {
    if (ft.x >= W.x1 || ft.y >= W.y1 || ft.x + ft.w <= W.x0 || ft.y + ft.h <= W.y0) continue;
    const x = X(ft.x), y = Y(ft.y), w = ft.w * HT, h = ft.h * HT;
    if (ft.kind === 'stream') stream(g, R, ft);
    else if (ft.kind === 'pilgrim') pilgrimStair(g, R, ft);
    else if (ft.kind === 'inari') { pathStroke(g, R, ft, 34, alpha(K.night, 0.3)); pathStroke(g, R, ft, 28, CK.gravel); pathStroke(g, R, ft, 2, SK.stoneDk, [8, 8]); }
    else if (ft.kind === 'bamboo') { pathStroke(g, R, ft, 26, alpha(K.night, 0.3)); pathStroke(g, R, ft, 20, CK.litter); pathStroke(g, R, ft, 12, mix(CK.litter, SK.stone, 0.3), [10, 6]); }
    else if (ft.kind === 'tea') teaField(g, R, ft);
    else if (ft.kind === 'meadow') lawn(g, x, y, w, h, mix(CK.grass, CK.floor, 0.3), CK.grassHi, CK.grassLo, true, 7);
    else if (ft.kind === 'garden') {
      lawn(g, x, y, w, h, mix(SK.mossLo, CK.floor, 0.35), SK.mossHi, SK.mossLo, false, 11);
      // a raked gravel walk round the pond, the pond and its bridge, stepping stones to the tea house
      g.save(); g.strokeStyle = mix(K.white, SK.stoneHi, 0.4); g.lineWidth = 22; g.beginPath(); g.ellipse(x + 9.5 * HT, y + 8 * HT, 7.6 * HT, 6.4 * HT, 0, 0, Math.PI * 2); g.stroke();
      g.strokeStyle = dk(mix(K.white, SK.stoneHi, 0.4), 0.2); g.lineWidth = 1; for (const o of [-7, 0, 7]) { g.beginPath(); g.ellipse(x + 9.5 * HT, y + 8 * HT, 7.6 * HT + o, 6.4 * HT + o, 0, 0, Math.PI * 2); g.stroke(); } g.restore();
      pond(g, X(279) - 6, Y(115) - 8, 9 * HT + 12, 5 * HT + 16);
      redBridge(g, X(283), Y(114) + 8, 2 * HT, 6 * HT - 16);
      steppingStones(g, [[X(288), Y(121)], [X(289) + 4, Y(120) + 6], [X(290) + 8, Y(119)], [X(290) + 4, Y(117) + 6], [X(289) + 16, Y(115) + 10]]);
    } else if (ft.kind === 'onsen') {
      onsenPool(g, X(306), Y(113), 5 * HT, 4 * HT);
      fenceH(g, x, y + h - 10, w); fenceV(g, x, y, h - 10); fenceV(g, x + w - 8, y, h - 10);
      px(g, x + w / 2 - 20, y + h - 10, 40, 12, K.woodLo); // the gap in the fence where the path comes in
    } else if (ft.kind === 'reststop') {
      const p = WORLD.plazas.find(q => q.name === 'Rest stop'); if (p) restStop(g, X(p.x), Y(p.y), p.w * HT, p.h * HT);
    }
  }
  if (road) ribbon(g, R);
}

// ================================================================== upright sprites (cached)
interface Sprite { cv: HTMLCanvasElement; ax: number; ay: number }
const cache = new Map<string, Sprite>();
const memo = (k: string, f: () => Sprite) => cache.get(k) ?? (cache.set(k, f()), cache.get(k)!);
/** a crown of overlapping lobes: dark rim, mid body, lit top-left, a few bright dabs */
function lobes(g: G, cx: number, cy: number, rx: number, ry: number, n: number, seed: number, c0: string, c1: string, c2: string, hi: string) {
  const L: [number, number, number][] = [];
  for (let k = 0; k < n; k++) { const a = hash2(k, seed) * Math.PI * 2, r = 0.25 + hash2(seed, k) * 0.6; L.push([cx + Math.cos(a) * rx * r * 0.7, cy + Math.sin(a) * ry * r * 0.7, 0.45 + hash2(k + 9, seed) * 0.3]); }
  L.push([cx, cy, 0.7]);
  for (const [x, y, s] of L) disc(g, Math.round(x), Math.round(y), Math.round(rx * s) + 1, Math.round(ry * s) + 1, K.ink);
  for (const [x, y, s] of L) disc(g, Math.round(x), Math.round(y), Math.round(rx * s), Math.round(ry * s), c0);
  for (const [x, y, s] of L) disc(g, Math.round(x - rx * s * 0.15), Math.round(y - ry * s * 0.2), Math.round(rx * s * 0.75), Math.round(ry * s * 0.7), c1);
  for (const [x, y, s] of L) disc(g, Math.round(x - rx * s * 0.3), Math.round(y - ry * s * 0.38), Math.round(rx * s * 0.38), Math.round(ry * s * 0.32), c2);
  speckle(g, Math.round(cx - rx), Math.round(cy - ry), Math.round(rx * 1.4), Math.round(ry * 1.2), hi, 0.03, seed, 2);
}
function trunk(g: G, x: number, y0: number, y1: number, w: number) {
  px(g, x - 1, y0, w + 2, y1 - y0, K.ink); px(g, x, y0, w, y1 - y0, CK.bark); px(g, x, y0, Math.max(1, w >> 2), y1 - y0, CK.barkHi);
}
function sakura(v: number): Sprite {
  return memo(`sak${Math.round(v * 3)}`, () => {
    const W = 76, H = 82, [cv, g] = canvas(W, H), ax = 38, ay = 79, s = Math.round(v * 3);
    shadow(g, 18, 74, 40, 7, 0.4);
    trunk(g, 35, 40, 78, 6); px(g, 30, 46, 8, 3, K.ink); px(g, 40, 50, 10, 3, K.ink); // the trunk forks
    lobes(g, 38, 30, 34, 25, 11, 5 + s, CK.sakDk, CK.sakLo, CK.sak, CK.sakHi);
    speckle(g, 6, 6, 64, 48, CK.sakHi, 0.04, 9 + s, 2);
    for (let k = 0; k < 7; k++) px(g, 8 + Math.floor(hash2(k, s) * 60), 56 + Math.floor(hash2(s, k) * 20), 2, 1, CK.sak); // petals under it
    return { cv, ax, ay };
  });
}
function maple(v: number): Sprite {
  return memo(`map${Math.round(v * 3)}`, () => {
    const W = 64, H = 84, [cv, g] = canvas(W, H), ax = 32, ay = 81, s = Math.round(v * 3);
    shadow(g, 14, 76, 36, 6, 0.4); trunk(g, 30, 38, 80, 5);
    lobes(g, 32, 30, 28, 24, 10, 31 + s, CK.mapleLo, CK.maple, CK.mapleHi, lt(CK.mapleHi, 0.35));
    return { cv, ax, ay };
  });
}
function broad(v: number): Sprite {
  return memo(`brd${Math.round(v * 3)}`, () => {
    const W = 70, H = 96, [cv, g] = canvas(W, H), ax = 35, ay = 93, s = Math.round(v * 3);
    shadow(g, 14, 88, 42, 7, 0.4); trunk(g, 32, 44, 92, 6);
    lobes(g, 35, 34, 32, 28, 12, 61 + s, CK.broadLo, CK.broad, CK.broadHi, lt(CK.broadHi, 0.3));
    return { cv, ax, ay };
  });
}
function pine(v: number): Sprite {
  return memo(`pine${Math.round(v * 3)}`, () => {
    const W = 80, H = 104, [cv, g] = canvas(W, H), ax = 40, ay = 101, s = Math.round(v * 3);
    shadow(g, 18, 96, 46, 7, 0.4);
    // a leaning black-pine trunk with flat cloud-pruned pads of needles on its branches
    for (let j = 0; j < 70; j++) { const x = 38 + Math.round(Math.sin(j / 18 + s) * 6); px(g, x - 1, 100 - j, 8, 1, K.ink); px(g, x, 100 - j, 6, 1, CK.bark); px(g, x, 100 - j, 2, 1, CK.barkHi); }
    const pads: [number, number, number][] = [[40, 20, 22], [20, 40, 16], [60, 46, 17], [34, 58, 14], [56, 26, 12]];
    for (const [x, y, r] of pads) { px(g, x - 2, y + 2, 4, 14, K.ink); disc(g, x, y + 2, r + 1, Math.round(r * 0.5) + 1, K.ink); disc(g, x, y + 2, r, Math.round(r * 0.5), CK.pineLo); disc(g, x - 2, y, r - 3, Math.round(r * 0.38), CK.pine); for (let i = -r + 3; i < r - 3; i += 4) px(g, x + i, y - 2 + ((i + s) & 1), 3, 1, CK.pineHi); }
    return { cv, ax, ay };
  });
}
function bamboo(v: number): Sprite {
  return memo(`bam${Math.round(v * 3)}`, () => {
    const W = 56, H = 150, [cv, g] = canvas(W, H), ax = 28, ay = 147, s = Math.round(v * 3);
    shadow(g, 10, 142, 36, 6, 0.35);
    for (let k = 0; k < 7; k++) { // stalks of a clump, slightly leaning, nodes every 14 px
      const bx = 10 + k * 5 + Math.floor(hash2(k, s) * 4), top = 6 + Math.floor(hash2(s, k) * 30), lean = (hash2(k + 3, s) - 0.5) * 10;
      for (let j = top; j < 146; j++) { const x = Math.round(bx + lean * (146 - j) / 146); px(g, x - 1, j, 5, 1, K.ink); px(g, x, j, 3, 1, (j - top) % 14 === 0 ? CK.bambooLo : CK.bamboo); px(g, x, j, 1, 1, CK.bambooHi); }
      for (let j = top + 4; j < 100; j += 9) { const x = Math.round(bx + lean * (146 - j) / 146), sd = (j + k) % 18 < 9 ? 1 : -1; for (let i = 0; i < 9; i++) px(g, x + sd * (3 + i), j - Math.floor(i / 3), 2, 1, i < 5 ? CK.bamboo : CK.bambooHi); }
    }
    return { cv, ax, ay };
  });
}
export function treeSprite(tr: Tree, cedarFn: (v: number) => Sprite): Sprite {
  switch (tr.kind) {
    case 'sakura': return sakura(tr.v); case 'maple': return maple(tr.v); case 'broad': return broad(tr.v);
    case 'pine': return pine(tr.v); case 'bamboo': return bamboo(tr.v); default: return cedarFn(Math.floor(tr.v * 3));
  }
}
function teaHouse(): Sprite {
  return memo('tea', () => {
    const W = 4 * HT + 12, H = 3 * HT + 60, [cv, g] = canvas(W, H), ax = W / 2, ay = H - 4, b = H - 6;
    shadow(g, 8, b - 4, W - 8, 10, 0.4);
    // thatched hip roof over a small timber room with shoji, a stone at the low crawl-in door (nijiriguchi)
    px(g, 10, b - 52, W - 20, 50, K.ink); px(g, 12, b - 50, W - 24, 46, CK.cedarWood);
    for (let i = 18; i < W - 18; i += 22) { px(g, i, b - 44, 18, 30, K.ink); px(g, i + 1, b - 43, 16, 28, mix(SK.paper, K.warmHi, 0.55)); px(g, i + 9, b - 43, 1, 28, K.wood); px(g, i + 1, b - 30, 16, 1, K.wood); }
    px(g, W / 2 - 10, b - 22, 20, 18, K.ink); px(g, W / 2 - 9, b - 21, 18, 16, K.woodLo);
    disc(g, W / 2, b - 1, 10, 4, K.ink); disc(g, W / 2, b - 2, 9, 3, SK.stone);
    for (let j = 0; j < 58; j++) { const ins = Math.round(Math.max(0, 40 - j) * 0.9); px(g, ins, 10 + j, W - 2 * ins, 1, j < 4 ? K.ink : j % 6 ? mix(K.woodHi, K.amber, 0.25) : K.woodLo); }
    px(g, 0, 64, W, 6, K.ink); px(g, 2, 64, W - 4, 3, mix(K.woodHi, K.amber, 0.3));
    px(g, W / 2 - 18, 6, 36, 8, K.ink); px(g, W / 2 - 17, 7, 34, 5, K.woodLo); // ridge
    return { cv, ax, ay };
  });
}
function onsenHut(): Sprite {
  return memo('hut', () => {
    const W = 5 * HT + 8, H = 2 * HT + 74, [cv, g] = canvas(W, H), ax = W / 2, ay = H - 4, b = H - 6;
    shadow(g, 6, b - 4, W - 6, 10, 0.4);
    px(g, 8, b - 48, W - 16, 46, K.ink); px(g, 10, b - 46, W - 20, 42, CK.hinoki);
    for (let i = 12; i < W - 12; i += 8) px(g, i, b - 46, 1, 42, mix(CK.hinoki, K.woodLo, 0.4));
    // two noren curtains (men's indigo, women's red) with the hot-spring mark
    for (const [nx, c] of [[W / 2 - 46, dk(K.cyan, 0.55)], [W / 2 + 6, SK.verm]] as const) {
      px(g, nx, b - 42, 40, 28, K.ink); px(g, nx + 1, b - 41, 38, 26, c); px(g, nx + 20, b - 41, 1, 26, K.ink);
      disc(g, nx + 20, b - 22, 7, 3, K.ink); disc(g, nx + 20, b - 23, 6, 2, SK.paper);
      for (const o of [-4, 0, 4]) for (let j = 0; j < 8; j++) px(g, nx + 20 + o + (j % 4 < 2 ? 0 : 1), b - 36 + j, 1, 1, SK.paper);
    }
    for (let j = 0; j < 40; j++) { const ins = Math.round(Math.max(0, 28 - j) * 0.9); px(g, ins, 22 + j, W - 2 * ins, 1, j < 3 ? K.ink : j % 5 ? NEW_KAWARA : dk(K.slate, 0.6)); }
    px(g, 0, 60, W, 5, K.ink);
    px(g, W - 30, b - 60, 4, 14, K.ink); disc(g, W - 28, b - 62, 6, 7, K.ink); disc(g, W - 28, b - 62, 4, 5, SK.verm); // a lantern on the eave
    return { cv, ax, ay };
  });
}
const NEW_KAWARA = dk(K.slate, 0.25);
function pickersRest(): Sprite {
  return memo('rest', () => {
    const W = 3 * HT + 16, H = 2 * HT + 32, [cv, g] = canvas(W, H), ax = W / 2, ay = H - 4, b = H - 6; // H keeps the roof (drawn from the top) resting on the wall posts, b - 44
    shadow(g, 6, b - 3, W - 8, 9, 0.4);
    // an open-fronted hut: plank back wall, corner posts, a bark-shingle roof on a low ridge; a bench and two picking baskets
    px(g, 8, b - 40, W - 16, 38, K.ink); px(g, 10, b - 38, W - 20, 34, CK.cedarWood);
    for (let i = 14; i < W - 12; i += 8) px(g, i, b - 38, 1, 34, mix(CK.cedarWood, K.woodLo, 0.45));
    for (const x of [8, W - 14]) { px(g, x, b - 44, 6, 44, K.ink); px(g, x + 1, b - 44, 4, 43, K.woodHi); }
    px(g, 20, b - 22, W - 40, 5, K.ink); px(g, 21, b - 21, W - 42, 3, CK.hinoki); px(g, 24, b - 17, 3, 15, K.ink); px(g, W - 27, b - 17, 3, 15, K.ink); // the bench
    for (const bx of [W / 2 - 4, W - 38]) { // woven baskets heaped with tea leaves
      disc(g, bx, b - 4, 10, 5, K.ink); px(g, bx - 10, b - 14, 20, 11, K.ink); px(g, bx - 9, b - 13, 18, 9, CK.bamboo);
      for (let i = -8; i < 9; i += 4) px(g, bx + i, b - 13, 1, 9, CK.bambooLo);
      disc(g, bx, b - 14, 9, 4, K.ink); disc(g, bx, b - 15, 8, 3, CK.tea); speckle(g, bx - 7, b - 18, 14, 5, CK.teaTip, 0.25, bx, 2);
    }
    for (let j = 0; j < 36; j++) { const ins = Math.round(Math.max(0, 22 - j) * 0.8); px(g, ins, 12 + j, W - 2 * ins, 1, j < 3 ? K.ink : j % 5 ? mix(K.woodHi, K.slate, 0.35) : K.woodLo); }
    px(g, 0, 46, W, 5, K.ink); px(g, 2, 46, W - 4, 2, mix(K.woodHi, K.amber, 0.2));
    px(g, W / 2 - 14, 8, 28, 7, K.ink); px(g, W / 2 - 13, 9, 26, 4, K.woodLo); // ridge
    px(g, W - 22, b - 46, 3, 12, K.ink); disc(g, W - 20, b - 48, 5, 6, K.ink); disc(g, W - 20, b - 48, 3, 4, SK.verm); // a paper lantern under the eave
    return { cv, ax, ay };
  });
}
function vending(): Sprite {
  return memo('vend', () => {
    const W = 2 * HT + 4, H = 82, [cv, g] = canvas(W, H), ax = W / 2, ay = H - 3;
    shadow(g, 2, H - 8, W, 8, 0.4);
    for (const [vx, body] of [[2, SK.paper], [HT + 2, dk(K.cyan, 0.3)]] as const) {
      px(g, vx, 6, HT - 2, H - 10, K.ink); px(g, vx + 1, 7, HT - 4, H - 12, body);
      px(g, vx + 4, 12, HT - 10, 30, K.ink); for (let r = 0; r < 3; r++) for (let c = 0; c < 4; c++) px(g, vx + 6 + c * 5, 14 + r * 9, 4, 7, [K.pink, K.amber, K.cyan, SK.paper][(r + c) % 4]);
      px(g, vx + 4, 46, HT - 10, 4, K.ink); px(g, vx + 6, 47, 4, 2, K.cyan);
      px(g, vx + 5, H - 20, HT - 12, 8, K.ink);
    }
    return { cv, ax, ay };
  });
}
function hokora(): Sprite {
  return memo('hokora', () => {
    const W = 3 * HT + 8, H = 2 * HT + 50, [cv, g] = canvas(W, H), ax = W / 2, ay = H - 4, b = H - 6;
    shadow(g, 6, b - 3, W - 8, 8, 0.4);
    px(g, 10, b - 16, W - 20, 14, K.ink); px(g, 11, b - 15, W - 22, 12, SK.stoneLo); // stone plinth
    px(g, 24, b - 46, W - 48, 32, K.ink); px(g, 25, b - 45, W - 50, 30, SK.verm); px(g, W / 2 - 8, b - 40, 16, 22, K.ink); px(g, W / 2 - 7, b - 39, 14, 20, SK.gold);
    for (let j = 0; j < 22; j++) { const ins = Math.round(Math.max(0, 16 - j)); px(g, 12 + ins, 30 + j, W - 24 - 2 * ins, 1, j < 2 ? K.ink : SK.vermLo); }
    // two kitsune on the plinth, white with red bibs, a key and a jewel in their mouths
    for (const fx of [16, W - 26]) { px(g, fx, b - 34, 10, 20, K.ink); px(g, fx + 1, b - 33, 8, 18, SK.paper); px(g, fx + 1, b - 37, 3, 4, K.ink); px(g, fx + 6, b - 37, 3, 4, K.ink); px(g, fx + 1, b - 26, 8, 3, SK.verm); }
    px(g, W / 2 - 3, b - 18, 6, 3, K.warmHi); // candles
    return { cv, ax, ay };
  });
}
function smallTorii(): Sprite {
  return memo('stor', () => {
    const W = 50, H = 58, [cv, g] = canvas(W, H), ax = 25, ay = 55;
    for (const x of [9, 35]) { shadow(g, x, 52, 10, 4, 0.35); px(g, x - 1, 12, 8, 43, K.ink); px(g, x, 12, 6, 43, SK.verm); px(g, x, 12, 2, 43, SK.vermHi); px(g, x, 47, 6, 8, K.ink); }
    px(g, 0, 4, W, 6, K.ink); px(g, 1, 5, W - 2, 3, K.ink); px(g, 2, 9, W - 4, 4, SK.verm); px(g, 4, 20, W - 8, 4, K.ink); px(g, 5, 21, W - 10, 2, SK.verm);
    return { cv, ax, ay };
  });
}
function waterfall(): Sprite {
  return memo('fall', () => {
    const W = 3 * HT + 16, H = 2 * HT + 70, [cv, g] = canvas(W, H), ax = W / 2, ay = H - 6;
    // a mossy granite cliff with a white fall down its middle into a plunge pool
    for (let k = 0; k < 9; k++) { const x = 8 + (k % 3) * 30, y = 16 + Math.floor(k / 3) * 28; disc(g, x + 14, y + 14, 18, 15, K.ink); disc(g, x + 14, y + 13, 16, 13, k % 2 ? SK.stoneLo : SK.stone); px(g, x + 4, y + 4, 12, 2, SK.stoneHi); speckle(g, x, y, 28, 26, SK.moss, 0.12, k, 2); }
    px(g, W / 2 - 10, 10, 20, H - 30, K.ink); px(g, W / 2 - 8, 10, 16, H - 30, mix(CK.waterHi, K.white, 0.55));
    for (let j = 14; j < H - 24; j += 5) px(g, W / 2 - 6 + (j % 10 ? 0 : 6), j, 4, 3, K.white);
    disc(g, W / 2, H - 16, 30, 10, K.ink); disc(g, W / 2, H - 16, 28, 8, CK.water); disc(g, W / 2, H - 18, 18, 5, CK.foam);
    return { cv, ax, ay };
  });
}
function bench(): Sprite {
  return memo('bench', () => {
    const [cv, g] = canvas(44, 22), ax = 22, ay = 20;
    shadow(g, 2, 16, 42, 5, 0.35); px(g, 2, 6, 40, 8, K.ink); px(g, 3, 7, 38, 6, K.woodHi); px(g, 3, 7, 38, 1, lt(K.woodHi, 0.3)); px(g, 6, 14, 3, 6, K.ink); px(g, 35, 14, 3, 6, K.ink);
    return { cv, ax, ay };
  });
}

/** a frost fan: the tall pole with a small wind machine on top that tea growers use to keep the cold air off the new leaves */
function frostFan(): Sprite {
  return memo('fan', () => {
    const W = 36, H = 78, [cv, g] = canvas(W, H), ax = W / 2, ay = H - 4;
    shadow(g, 9, H - 9, 18, 6, 0.4);
    px(g, W / 2 - 2, 16, 4, H - 22, K.ink); px(g, W / 2 - 1, 16, 2, H - 22, K.slate); px(g, W / 2 - 1, 16, 1, H - 22, SK.stoneHi); // the pole
    px(g, W / 2 - 5, H - 12, 10, 6, K.ink); px(g, W / 2 - 4, H - 11, 8, 4, SK.stoneLo); // its concrete foot
    px(g, W / 2 - 6, 8, 12, 9, K.ink); px(g, W / 2 - 5, 9, 10, 7, SK.stoneLo); px(g, W / 2 - 5, 9, 10, 2, SK.stoneHi); // the motor housing
    disc(g, W / 2, 12, 16, 3, K.ink); disc(g, W / 2, 12, 15, 2, SK.stone); disc(g, W / 2 - 4, 11, 7, 1, SK.stoneHi); // the blade, turning edge-on
    px(g, W / 2 - 1, 4, 2, 6, K.ink); px(g, W / 2 - 1, 4, 2, 2, SK.verm); // a red tip on the hub
    return { cv, ax, ay };
  });
}
/** a picker's woven basket of fresh leaves, left on the path */
function teaBasket(): Sprite {
  return memo('basket', () => {
    const [cv, g] = canvas(26, 22), ax = 13, ay = 19;
    shadow(g, 2, 15, 22, 5, 0.4);
    px(g, 3, 8, 20, 11, K.ink); px(g, 4, 9, 18, 9, CK.bamboo); for (let i = 5; i < 22; i += 4) px(g, i, 9, 1, 9, CK.bambooLo); px(g, 4, 9, 18, 1, CK.bambooHi);
    disc(g, 13, 8, 10, 4, K.ink); disc(g, 13, 7, 9, 3, CK.tea); speckle(g, 5, 4, 16, 6, CK.teaTip, 0.3, 4, 2); // heaped leaves
    return { cv, ax, ay };
  });
}

// ================================================================== placing props and lights
type Prop = (cv: HTMLCanvasElement, wx: number, wy: number, ax: number, ay: number) => void;
export function countryProps(m: GameMap, R: Rect, prop: Prop, lights: HdLight[], cedarFn: (v: number) => Sprite, toroFn: (lit: boolean) => Sprite, toriiFn: (span: number, broken: boolean) => Sprite) {
  const inR = (wx: number, wy: number) => wx >= R.x * TILE && wy >= R.y * TILE && wx < (R.x + R.w) * TILE && wy < (R.y + R.h) * TILE;
  const put = (s: Sprite, wx: number, wy: number) => { if (inR(wx, wy)) prop(s.cv, wx, wy, s.ax, s.ay); };
  const glow = (wx: number, wy: number, w: number, h: number, color: string, a: number, pool = false) => { if (inR(wx, wy)) lights.push({ x: wx - w / 2, y: wy - h / 2, w, h, color, alpha: a, pool }); };
  const plan = countryPlan(m);
  for (const tr of plan.trees) put(treeSprite(tr, cedarFn), Math.round(tr.x), Math.round(tr.y));
  for (const l of plan.lanterns) { put(toroFn(true), Math.round(l.x), Math.round(l.y)); glow(l.x, l.y - 18, 10, 8, K.amber, 0.55); glow(l.x, l.y + 4, 56, 30, K.amber, 0.42, true); }
  const road = WORLD.sanctuary?.road;
  if (road) for (const q of plan.torii) { // over the road where it runs north-south: posts just outside the asphalt
    const span = (road.half * 2 + 0.6) * TILE * 2;
    put(toriiFn(Math.round(span), false), Math.round(q.x * TILE), Math.round(q.y * TILE + 6));
  }
  const tile = (x: number, y: number) => ({ x: x * TILE, y: y * TILE });
  for (const s of WORLD.sanctuary?.solid ?? []) {
    const name = (s as { _?: string })._ ?? '', foot = tile(s.x + s.w / 2, s.y + s.h);
    if (name === 'tea house') { put(teaHouse(), foot.x, foot.y); glow(foot.x, foot.y - 18, 60, 20, K.amber, 0.4); glow(foot.x, foot.y + 10, 70, 26, K.amber, 0.3, true); }
    else if (name === 'onsen changing hut') { put(onsenHut(), foot.x, foot.y); glow(foot.x + 30, foot.y - 30, 12, 12, K.red, 0.5); glow(foot.x, foot.y + 12, 80, 28, K.amber, 0.3, true); }
    else if (name === 'pickers rest') { put(pickersRest(), foot.x, foot.y); glow(foot.x + 24, foot.y - 36, 12, 12, K.amber, 0.45); glow(foot.x, foot.y + 10, 70, 24, K.amber, 0.25, true); }
    else if (name === 'rest stop vending machines') { put(vending(), foot.x, foot.y); glow(foot.x, foot.y - 18, 34, 30, K.white, 0.55); glow(foot.x, foot.y - 6, 70, 30, K.cyan, 0.4, true); put(bench(), foot.x + 30, foot.y - 2); }
    else if (name === 'fox shrine hokora') { put(hokora(), foot.x, foot.y); glow(foot.x, foot.y - 8, 16, 10, K.amber, 0.6); glow(foot.x, foot.y + 10, 50, 22, K.amber, 0.35, true); }
    else if (name === 'waterfall rock') { put(waterfall(), foot.x, foot.y + 6); glow(foot.x, foot.y + 6, 50, 18, K.cyan, 0.25, true); }
  }
  const tea = FEATURES().find(f => f.kind === 'tea');
  if (tea) { // frost fans stand in the earth paths between the rows; two baskets wait on the pickers' path
    for (const [x, gap] of [[262.4, 5], [266.4, 3], [270.4, 1]]) put(frostFan(), Math.round(x * TILE), Math.round(((teaRowY(tea, gap, x) + teaRowY(tea, gap + 1, x)) / 2 + 0.3) * TILE));
    for (const [x, y] of [[268.3, 98.5], [265.3, 97.1]]) put(teaBasket(), Math.round(x * TILE), Math.round(y * TILE));
  }
  const inari = FEATURES().find(f => f.kind === 'inari');
  if (inari) { const S = featurePath(inari), st = smallTorii(); for (let i = 0; i < S.length; i += 5) if (i > 4) put(st, Math.round(S[i].x * TILE), Math.round(S[i].y * TILE + 4)); }
}


